import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Building2, Search, Filter, X, ChevronRight, 
  MapPin, Image, Target, DollarSign, Users, MoreHorizontal,
  Pause, Play, Trash2, AlertTriangle, Ban, Loader2
} from "lucide-react";

const tiers = [
  { value: "GOLD", label: "Gold", color: "bg-yellow-500" },
  { value: "SILVER", label: "Silver", color: "bg-gray-400" },
  { value: "BRONZE", label: "Bronze", color: "bg-orange-600" },
];

const statuses = [
  { value: "ACTIVE", label: "Active", color: "bg-green-500" },
  { value: "INACTIVE", label: "Inactive", color: "bg-gray-500" },
  { value: "SUSPENDED", label: "Suspended", color: "bg-red-500" },
  { value: "TRIAL", label: "Trial", color: "bg-blue-500" },
  { value: "PENDING", label: "Pending", color: "bg-yellow-500" },
];

const australianStates = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "NT", label: "Northern Territory" },
  { value: "ACT", label: "Australian Capital Territory" },
];

export default function AdvertisersList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Action modal states
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'suspend', 'disable', 'delete'
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAdvertisers();
  }, []);

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdvertisers(response.data);
    } catch (error) {
      toast.error("Failed to fetch advertisers");
    } finally {
      setLoading(false);
    }
  };

  // Action handlers for admin management
  const openActionModal = (advertiser, action) => {
    setSelectedAdvertiser(advertiser);
    setPendingAction(action);
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedAdvertiser(null);
    setPendingAction(null);
  };

  const handleStatusChange = async (accountId, newStatus) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/accounts/${accountId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Account ${newStatus.toLowerCase()} successfully`);
      closeActionModal();
      fetchAdvertisers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update account status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/accounts/${accountId}?confirm=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Account and associated data archived successfully");
      closeActionModal();
      fetchAdvertisers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAction = () => {
    if (!selectedAdvertiser) return;
    
    switch (pendingAction) {
      case 'suspend':
        handleStatusChange(selectedAdvertiser.id, 'SUSPENDED');
        break;
      case 'disable':
        handleStatusChange(selectedAdvertiser.id, 'INACTIVE');
        break;
      case 'activate':
        handleStatusChange(selectedAdvertiser.id, 'ACTIVE');
        break;
      case 'delete':
        handleDeleteAccount(selectedAdvertiser.id);
        break;
      default:
        closeActionModal();
    }
  };

  const getActionConfig = () => {
    switch (pendingAction) {
      case 'suspend':
        return {
          title: 'Suspend Account',
          description: `Are you sure you want to suspend "${selectedAdvertiser?.business_name}"? This will pause all active campaigns and prevent login.`,
          buttonText: 'Suspend Account',
          variant: 'warning'
        };
      case 'disable':
        return {
          title: 'Disable Account',
          description: `Are you sure you want to disable "${selectedAdvertiser?.business_name}"? This will deactivate the account and all campaigns will be paused.`,
          buttonText: 'Disable Account',
          variant: 'warning'
        };
      case 'activate':
        return {
          title: 'Activate Account',
          description: `Reactivate "${selectedAdvertiser?.business_name}"? This will restore account access.`,
          buttonText: 'Activate Account',
          variant: 'default'
        };
      case 'delete':
        return {
          title: 'Delete Account',
          description: `WARNING: This will permanently archive "${selectedAdvertiser?.business_name}" and all associated data (campaigns, creatives, locations). This action cannot be undone.`,
          buttonText: 'Delete Forever',
          variant: 'destructive'
        };
      default:
        return { title: '', description: '', buttonText: '', variant: 'default' };
    }
  };

  // Filtered advertisers
  const filteredAdvertisers = useMemo(() => {
    return advertisers.filter(adv => {
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          adv.business_name?.toLowerCase().includes(query) ||
          adv.abn?.includes(query) ||
          adv.primary_location?.suburb?.toLowerCase().includes(query) ||
          adv.primary_contact?.email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Tier filter
      if (filterTier !== "all" && adv.tier !== filterTier) return false;

      // Status filter
      if (filterStatus !== "all" && adv.status !== filterStatus) return false;

      // State filter - use primary_location
      if (filterState !== "all" && adv.primary_location?.state !== filterState) return false;

      return true;
    });
  }, [advertisers, searchQuery, filterTier, filterStatus, filterState]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterTier("all");
    setFilterStatus("all");
    setFilterState("all");
  };

  const hasActiveFilters = searchQuery || filterTier !== "all" || filterStatus !== "all" || filterState !== "all";

  const getTierBadge = (tier) => {
    const t = tiers.find(x => x.value === tier);
    return t ? (
      <Badge className={`${t.color} text-white text-xs`}>{t.label}</Badge>
    ) : null;
  };

  const getStatusBadge = (status) => {
    const s = statuses.find(x => x.value === status);
    return s ? (
      <Badge variant="outline" className="text-xs">
        <span className={`w-2 h-2 rounded-full ${s.color} mr-1.5`}></span>
        {s.label}
      </Badge>
    ) : null;
  };

  const handleOpenAdvertiser = (advertiser) => {
    navigate(`/admin/advertisers/${advertiser.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading advertisers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="advertisers-list">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Advertisers</h1>
          <p className="text-muted-foreground mt-1">
            Manage all advertiser accounts and their campaigns
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{advertisers.length} total advertisers</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiers.map(tier => {
          const count = advertisers.filter(a => a.tier === tier.value).length;
          return (
            <Card key={tier.value} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{tier.label}</span>
                  <Badge className={`${tier.color} text-white`}>{count}</Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search and Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by business name, ABN, suburb, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-advertisers"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-primary/10" : ""}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {[filterTier !== "all", filterStatus !== "all", filterState !== "all"].filter(Boolean).length}
                  </Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} size="icon">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-border">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Tier</Label>
                  <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Tiers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      {tiers.map(tier => (
                        <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {statuses.map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Select value={filterState} onValueChange={setFilterState}>
                    <SelectTrigger>
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {australianStates.map(state => (
                        <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>Showing {filteredAdvertisers.length} of {advertisers.length} advertisers</span>
      </div>

      {/* Advertisers List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredAdvertisers.map(advertiser => (
              <div 
                key={advertiser.id} 
                className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors group"
                data-testid={`advertiser-row-${advertiser.id}`}
              >
                {/* Left: Icon + Business Info */}
                <div 
                  className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleOpenAdvertiser(advertiser)}
                >
                  <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{advertiser.business_name}</h3>
                      {getTierBadge(advertiser.tier)}
                      {getStatusBadge(advertiser.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {advertiser.primary_location ? (
                        <>
                          {advertiser.primary_location.suburb}, {advertiser.primary_location.state}
                          {advertiser.primary_location.is_default && (
                            <span className="text-yellow-500 ml-1" title="No primary location set - showing first location">⚠</span>
                          )}
                        </>
                      ) : (
                        <span className="text-yellow-500">No location set</span>
                      )}
                      {" "}• ABN: {advertiser.abn}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {advertiser.location_count || 0} locations
                      </span>
                      <span className="flex items-center gap-1">
                        <Image className="h-3 w-3" />
                        {advertiser.creative_count || 0} creatives
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        {advertiser.campaign_count || 0} campaigns
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenAdvertiser(advertiser)}>
                        <ChevronRight className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {advertiser.status === 'ACTIVE' || advertiser.status === 'TRIAL' ? (
                        <DropdownMenuItem onClick={() => openActionModal(advertiser, 'suspend')}>
                          <Pause className="mr-2 h-4 w-4" />
                          Suspend Account
                        </DropdownMenuItem>
                      ) : advertiser.status === 'SUSPENDED' ? (
                        <DropdownMenuItem onClick={() => openActionModal(advertiser, 'activate')}>
                          <Play className="mr-2 h-4 w-4" />
                          Reactivate Account
                        </DropdownMenuItem>
                      ) : null}
                      {advertiser.status !== 'INACTIVE' && (
                        <DropdownMenuItem onClick={() => openActionModal(advertiser, 'disable')}>
                          <Ban className="mr-2 h-4 w-4" />
                          Disable Account
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => openActionModal(advertiser, 'delete')}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Account
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ChevronRight 
                    className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors cursor-pointer" 
                    onClick={() => handleOpenAdvertiser(advertiser)}
                  />
                </div>
              </div>
            ))}
          </div>

          {filteredAdvertisers.length === 0 && advertisers.length > 0 && (
            <div className="p-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No matching advertisers</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your search or filters</p>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          )}

          {advertisers.length === 0 && (
            <div className="p-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No advertisers yet</h3>
              <p className="text-muted-foreground">Advertisers will appear here once they sign up</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Confirmation Modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingAction === 'delete' && <AlertTriangle className="h-5 w-5 text-destructive" />}
              {(pendingAction === 'suspend' || pendingAction === 'disable') && <AlertTriangle className="h-5 w-5 text-amber-500" />}
              {pendingAction === 'activate' && <Play className="h-5 w-5 text-green-500" />}
              {getActionConfig().title}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {getActionConfig().description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeActionModal} disabled={actionLoading}>
              Cancel
            </Button>
            <Button 
              variant={pendingAction === 'delete' ? 'destructive' : pendingAction === 'activate' ? 'default' : 'outline'}
              onClick={confirmAction}
              disabled={actionLoading}
              className={pendingAction === 'suspend' || pendingAction === 'disable' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {getActionConfig().buttonText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
